import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { DossierService } from '../../services/dossier.service';
import { SignatureStatus } from '../../models/dossier.model';

/** Shown when the upload has no signature data to select. Distinct from a
 *  failed fetch, which sets phase='error'. */
const EMPTY_STATUS: SignatureStatus = {
  overallStatus: 'NONE',
  signatureCount: 0,
  verifiedAt: null,
  trustedLists: null,
  signatures: [],
};

/**
 * Digital-signature indicator for an uploaded original — badge states adapted
 * from lib/digsig/SignatureBadge.jsx (a read-only reference from a different
 * project; not imported, ported to this app's own DossierService/model). Backed
 * by dd_signatures (pyHanko): real cryptographic validity, trust-chain checking
 * against the EU Trusted Lists, and an eIDAS qualification verdict. The badge
 * shows only the three-state verdict; the qualification detail lives in the
 * panel it opens.
 *
 * Fetches lazily on init and caches nothing itself; the parent decides when a
 * badge exists (one per single-PDF upload, or one per file in a ZIP upload).
 */
@Component({
  selector: 'app-signature-badge',
  templateUrl: './signature-badge.component.html',
  styleUrls: ['./signature-badge.component.css'],
})
export class SignatureBadgeComponent implements OnChanges {
  /** Dossier the upload belongs to. Ignored when preloaded is given. */
  @Input() dossierId?: string;
  /** The upload to fetch signature status for. Ignored when preloaded is given. */
  @Input() uploadId?: string;
  /** Set only when this badge represents one file inside a batch/ZIP upload —
   *  selects that file's entry out of the upload's per-file signature list.
   *  Ignored when preloaded is given. */
  @Input() filename: string | null = null;
  /** Pre-fetched result — for a batch upload with several badges, the parent
   *  fetches the file list once and passes each entry down, rather than every
   *  badge independently re-fetching the same upload's signature data. */
  @Input() preloaded: SignatureStatus | null = null;

  @Output() open = new EventEmitter<SignatureStatus>();

  phase: 'loading' | 'done' | 'error' = 'loading';
  result: SignatureStatus | null = null;

  constructor(private dossierService: DossierService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preloaded']) {
      this.result = this.preloaded;
      this.phase = this.preloaded ? 'done' : 'loading';
      return;
    }
    if (changes['dossierId'] || changes['uploadId'] || changes['filename']) {
      this.load();
    }
  }

  private load(): void {
    if (this.preloaded !== null) return;
    if (!this.dossierId || !this.uploadId) return;
    this.phase = 'loading';
    this.result = null;
    this.dossierService.getUploadSignature(this.dossierId, this.uploadId).subscribe({
      next: (data) => {
        if (this.filename !== null && 'files' in data) {
          const file = data.files.find((f) => f.filename === this.filename);
          this.result = file ?? EMPTY_STATUS;
        } else if (!('files' in data)) {
          this.result = data;
        } else {
          // A batch result came back but no filename was given to select from
          // it — nothing to show rather than guessing which file.
          this.result = EMPTY_STATUS;
        }
        this.phase = 'done';
      },
      error: () => {
        this.phase = 'error';
      },
    });
  }

  get hasSignature(): boolean {
    return (this.result?.signatureCount ?? 0) > 0;
  }

  onClick(): void {
    if (this.hasSignature && this.result) {
      this.open.emit(this.result);
    }
  }
}
